import { RefObject, useLayoutEffect, useRef, useState } from "react";

const DEFAULT_SEPARATOR = ", ";
const DEFAULT_FORMAT_MORE = (count: number) => `+${count} more`;

interface UseFittingItemsOptions {
  items: string[];
  separator?: string;
  formatMore?: (count: number) => string;
}

interface UseFittingItemsResult {
  containerRef: RefObject<HTMLDivElement | null>;
  measureRef: RefObject<HTMLSpanElement | null>;
  visibleItems: string[];
  hiddenItems: string[];
  hasHidden: boolean;
  displayText: string;
}

/**
 * Builds display text for a given number of visible items.
 */
function buildDisplayText(items: string[], visibleCount: number): string {
  const visible = items.slice(0, visibleCount).join(DEFAULT_SEPARATOR);
  const hiddenCount = items.length - visibleCount;

  if (hiddenCount > 0) {
    return `${visible}${DEFAULT_SEPARATOR}${DEFAULT_FORMAT_MORE(hiddenCount)}`;
  }
  return visible;
}

/**
 * Hook to measure how many items fit within a container width.
 * Items that don't fit can be shown in a tooltip.
 */
export function useRoleCellFittingItems({
  items,
}: UseFittingItemsOptions): UseFittingItemsResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measureEl = measureRef.current;

    if (!container || !measureEl || items.length === 0) return;

    const containerWidth = container.offsetWidth;

    // Find how many items fit
    let fittingCount = 1;
    for (let i = 1; i <= items.length; i++) {
      measureEl.textContent = buildDisplayText(items, i);

      if (measureEl.offsetWidth <= containerWidth) {
        fittingCount = i;
      } else {
        break;
      }
    }

    setVisibleCount((prev) => (prev !== fittingCount ? fittingCount : prev));
  }, [items]);

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);
  const hasHidden = hiddenItems.length > 0;

  const displayText =
    items.length === 0 ? "" : buildDisplayText(items, visibleCount);

  return {
    containerRef,
    measureRef,
    visibleItems,
    hiddenItems,
    hasHidden,
    displayText,
  };
}
